// Admin path configuration — baked in at build time via VITE_ADMIN_PATH.
//
// SECURITY: there is intentionally NO short fallback. If VITE_ADMIN_PATH is
// unset or too short/weak, we treat admin routing as disabled: the router
// skips registering any /admin/* routes, so bots and attackers probing
// predictable URLs like `/admin/login`, `/admin`, or `/administrator`
// just get the public 404.
//
// The operator is expected to set VITE_ADMIN_PATH to a long, random slug
// (e.g. `admin-8f3kQ2mPx9vL7wR4`) in their production .env before building.

const MIN_ADMIN_PATH_LENGTH = 12;

// Paths we refuse to honour even if somebody sets the env var, because they
// defeat the point of having an obscured admin URL at all.
const WEAK_ADMIN_PATHS = new Set([
  'admin',
  'administrator',
  'admins',
  'login',
  'signin',
  'sign-in',
  'superadmin',
  'super-admin',
  'root',
  'wp-admin',
  'cpanel',
]);

const raw = (import.meta.env.VITE_ADMIN_PATH || '').trim().replace(/^\/+|\/+$/g, '');

const isStrongEnough =
  raw.length >= MIN_ADMIN_PATH_LENGTH && !WEAK_ADMIN_PATHS.has(raw.toLowerCase());

let _adminPath: string = isStrongEnough ? raw : '';

if (!isStrongEnough && import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.warn(
    '[admin] VITE_ADMIN_PATH is missing or too weak (must be >= ' +
      MIN_ADMIN_PATH_LENGTH +
      ' chars). Admin routes are disabled.'
  );
}

/** Returns the configured admin slug, or an empty string if none is set. */
export function getAdminPath(): string {
  return _adminPath;
}

/** True only when a strong admin path is configured at build time. */
export function isAdminPathConfigured(): boolean {
  return _adminPath.length > 0;
}

/**
 * Intended for tests / hot-reload. Setting an invalid path at runtime
 * behaves the same as the build-time path being unset: admin is disabled.
 */
export function setAdminPath(path: string) {
  const cleaned = (path || '').trim().replace(/^\/+|\/+$/g, '');
  const strong =
    cleaned.length >= MIN_ADMIN_PATH_LENGTH && !WEAK_ADMIN_PATHS.has(cleaned.toLowerCase());
  _adminPath = strong ? cleaned : '';
}
